// operations
// 1つ以上の action を組み合わせたもの。
// 個別の action は単純なままに、必要に応じて複雑な operations を作成できる。
// redux-thunkなどのミドルウェアはここで使う。
// import {push} from 'connected-react-router';

import {Amplify, Auth} from 'aws-amplify'; // Amplify
import appConfig from '@/config';
import { clearSession } from '@/common/Session';
import { commonOperations } from '@/reducks/common';
import { getCurrentUser } from '@/reducks/common/operations';
import {
  doLogin,
  doLogout,
  getUserInfo,
  setAuthorizedHash,
} from './actions';

// Amplify設定
if (appConfig.amplify) {
  Amplify.configure(appConfig.amplify);
}

// storeリセット
const doClear = (info) => {
  return (dispatch) => {
    dispatch(doLogout());
  };
};

/**
 * Cognito認証ログイン。
 *
 * @return {'OK'|'NO_LOGIN'|'SYS_ERROR'} 実行結果
 */
const doLoginOperation = () => {
  return async (dispatch, getState) => {
    const {form} = getState();
    const values = form.Login.values;

    // 入力値が取得できない場合は入力エラー
    if (!values?.username || !values.password) {
      console.error('Get Values Error.');
      return 'NO_LOGIN';
    }
    const userName = values.username ? values.username.trim() : null;
    const password = values.password ? values.password.trim() : null;

    // トリム後のユーザID/パスワードが空の場合はエラー
    if (!userName || !password) {
      console.error('ID/Password Empty Error.');
      return 'NO_LOGIN';
    }

    // ログイン認証
    try {
      console.log('ログイン認証', `user=${userName}`);
      dispatch(commonOperations.startRequest());
      const retLogin = await Auth.signIn(userName, password);
      dispatch(commonOperations.endRequest());

      // ログイン情報取得
      const attributes = retLogin.attributes;
      const corpCd = attributes ? attributes['custom:corpCd'] : null;
      const sub = attributes ? attributes.sub : null;
      const session = retLogin.signInUserSession;
      const idToken = session ? session.idToken.jwtToken : null;
      const loginInfo = {
        isLoggedIn: true,
        idToken: idToken,
        cognitoUser: sub,
        corpCd: corpCd,
        userId: retLogin.username,
      };
      console.log('ログイン情報',
          `user=${loginInfo.userId}`,
          `cognite=${loginInfo.cognitoUser}`);

      // 仮パスワードログイン時はAuth.signInの結果を保持
      let loginResult = 'OK';
      if (retLogin.challengeName === 'NEW_PASSWORD_REQUIRED') {
        loginInfo['signInUser'] = retLogin;
        loginResult = 'REDIRECT_NEW_PASSWORD';
      }

      // ログイン情報保存
      dispatch(doLogin(loginInfo));
      return loginResult;

    } catch (err) {
      console.log('signIn err:', err);
      dispatch(commonOperations.endRequest());
      if (err.code === 'UserNotFoundException' || err.code === 'NotAuthorizedException') {
        return 'NO_LOGIN';
      } else if (err.code === 'PasswordResetRequiredException') {
        return 'RESET_REQUIRE';
      } else {
        return 'SYS_ERROR';
      }
    }
  };
};

const sendAuthenticationCodeOperation = (username) => {
  console.log('パスワードリセット(認証コード送付)');

  return async (dispatch) => {
    try {
      dispatch(commonOperations.startRequest());

      const result = await Auth.forgotPassword(username);
      console.dir(result);
      return 'OK';
    } catch (err) {
      console.log('forgotPassword err:', err);

      if (err.code === 'UserNotFoundException') {
        // ユーザIDが誤っていた場合は成功扱い
        return 'OK';
      } else if (err.code === 'NotAuthorizedException') {
        // 未認証のユーザの場合は成功扱い
        return 'OK';
      } else if (err.code === 'LimitExceededException') {
        // リセット回数過多
        return 'LIMIT_EXCEED';
      } else {
        return 'SYS_ERROR';
      }
    } finally {
      dispatch(commonOperations.endRequest());
    }
  };
};

// パスワードリセット(パスワード更新)
const doPasswordResetOperation = (userName, code, password) => {
  console.log('パスワードリセット(パスワード更新)');
  return async (dispatch) => {
    try {
      dispatch(commonOperations.startRequest());

      await Auth.forgotPasswordSubmit(userName, code, password);
      return 'OK';
    } catch (err) {
      console.error(err);

      if (err.code === 'CodeMismatchException') {
        return 'CODE_ERROR';
      } else if (err.code === 'ExpiredCodeException') {
        return 'CODE_EXPIRED';
      } else if (err.code === 'PasswordHistoryPolicyViolationException') { // 2024.12.24 add
        return 'HISTORY_PASSWORD';
      } else {
        return 'SYS_ERROR';
      }
    } finally {
      dispatch(commonOperations.endRequest());
    }
  };
};

/**
 * 仮パスワードでログイン時のパスワード変更
 * @param {Auth.CognitoUser} user
 * @param {string} password 新しいパスワード
 * @return {function}
 */
const doCompletePassword = (user, password) => {
  return async (dispatch) => {
    try {
      dispatch(commonOperations.startRequest());
      await Auth.completeNewPassword(user, password);
      return 'OK';
    } catch (err) {
      console.error(err);
      if (err.code) {
        return err.code;
      }

      return 'SYS_ERROR';
    } finally {
      dispatch(commonOperations.endRequest());
    }
  };
};

/**
 * パスワードの変更
 * @param {string} oldPassword 現在のパスワード
 * @param {string} newPassword 新しいパスワード
 * @return {function}
 */
const doChangePassword = (oldPassword, newPassword) => {
  return async (dispatch) => {
    try {
      dispatch(commonOperations.startRequest());
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, oldPassword, newPassword);
      return 'OK';
    } catch (err) {
      console.error(err);
      if (err.code) {
        return err.code;
      }

      return 'SYS_ERROR';
    } finally {
      dispatch(commonOperations.endRequest());
    }
  };
};

/**
 * ログアウト処理の実行。
 *
 * ストレージのクリアと、ログイン情報のクリアを行う。
 *
 * @param {Function} dispatch Dispatch
 */
const logoutFunc = (dispatch) => {
  clearSession();
  dispatch(doLogout());
};

// ログアウト
const doLogoutOperation = (navigate) => {
  return (dispatch) => {
    logoutFunc(dispatch);

    // Amplify
    console.debug('Sign out');
    Auth.signOut()
        .then((data) => console.debug('Sign out done', data))
        .catch((err) => console.error('Sign out fail', err));

    // ログイン画面に遷移
    if (navigate) {
      navigate('/login');
    }
  };
};

/**
 * ユーザー情報取得。
 * @param {boolean} force 強制取得フラグ
 */
const getUserInfoOperation = (force = false) => {
  return async (dispatch, getState) => {
    const {auth} = getState();

    // すでに取得済の場合は処理終了
    if (!force && auth.userInfo) {
      console.debug('ユーザー情報取得済');
      return;
    }
    console.groupCollapsed('ユーザー情報取得', `force=${force}`);

    // ユーザー情報取得
    try {
      const userInfo = await getCurrentUser();

      // ユーザ情報をストアへ登録
      dispatch(getUserInfo(userInfo));
    } finally {
      console.groupEnd();
    }
  };
};

// トークン更新
const doRefreshTokenOperation = () => {
  return async (dispatch, getState) => {
    const {auth} = getState();
    const loginInfo = auth.loginInfo;
    if (loginInfo) {
      try {
        const currentSession = await Auth.currentSession();
        const idToken = currentSession.getIdToken().getJwtToken();

        // IDトークン更新
        if (idToken !== loginInfo.idToken) {
          console.info('トークン更新', loginInfo);
          const newInfo = { ...loginInfo, idToken };
          dispatch(doLogin(newInfo));
        }
      } catch (err) {
        console.error('Failed to refresh token', err);
      }
    }
  };
};

/**
 * ユーザー情報変更検出ハッシュの設定。
 *
 * @param {string} authorizedHash
 * @return {function}
 */
const doSetAuthorizedHash = (authorizedHash) => {
  return (dispatch) => {
    dispatch(setAuthorizedHash(authorizedHash));
  };
};

export default {
  doLoginOperation,
  sendAuthenticationCodeOperation,
  doPasswordResetOperation,
  doCompletePassword,
  doChangePassword,
  doLogoutOperation,
  getUserInfoOperation,
  doRefreshTokenOperation,
  doClear,
  doSetAuthorizedHash,
};
